import type {CompRef, Pointer} from '@wix/document-services-types'
import {
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    type PointerPredicate,
    reportBIError,
    reportImageUploadError
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {fetchImageUrlsForBuilder, uploadImage} from './aiExtensionUtils'
import {getReportableFromError} from '@wix/document-manager-utils'
import {
    aiImageInjectionValidationErrorMessage,
    aiImageInjectionValidationErrorType,
    aiImageUnexpectedError,
    aiImageUnexpectedErrorMessage,
    aiImageUploadErrorMessage,
    aiImageUploadErrorType,
    UPLOAD_STATUS
} from './constants'
import {DATA_TYPES} from '../../constants/constants'
import {type IdMap, injectData, predicateBuilderComp} from './aiBuilderUtils'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import type {SchemaExtensionAPI} from '../schema/schema'
import _ from 'lodash'

export interface ImageGenerationServerlessResponseWithIdMap {
    idMap: IdMap
    data: Record<string, string>
}

export interface BuilderImageOutlineDataWithIdMap {
    outline: BuilderImageOutlineData
    idMap: IdMap
    oldUrlMap: Record<string, string>
}

interface ValidationResult {
    isValid: boolean
    reason: string
}

interface BuilderImageSectionData {
    images: {id: string; width: string; height: string}[]
}

interface BuilderTextSectionData {
    [componentName: string]: Record<string, string>
}

type SectionData = BuilderImageSectionData & BuilderTextSectionData

interface BuilderImageOutlineData {
    [section: string]: SectionData
}

export const getBuilderImageOutlineData = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer
): BuilderImageOutlineDataWithIdMap => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {builderComponents} = createExtArgs.extensionAPI as BuilderComponentsExtensionAPI
    const {innerElements} = createExtArgs.extensionAPI as InnerElementsExtensionAPI
    const {viewer} = createExtArgs.extensionAPI as ViewerExtensionAPI

    const predicateBuilder = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const getContentRecursively = (
        rootPointer: Pointer,
        targetPointer: Pointer,
        idMap: IdMap,
        oldUrlMap: Record<string, string>,
        typeCounter: Record<string, number>,
        sectionName: string | undefined,
        predicate: PointerPredicate,
        outline: any,
        toAdd = false
    ): void => {
        if (rootPointer.id === targetPointer.id) toAdd = true
        const type = components.getComponentType(rootPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            if (componentDefinition.isSection(type)) {
                const sectionNameByPointer = getSectionNameByPointer(
                    createExtArgs.extensionAPI,
                    rootPointer,
                    DATA_TYPES.naming
                )
                sectionName = getNextIdForPrefix(sectionNameByPointer, typeCounter)
            }
            const children = pointers.structure.getChildren(rootPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    targetPointer,
                    idMap,
                    oldUrlMap,
                    typeCounter,
                    sectionName,
                    predicate,
                    outline,
                    toAdd
                )
            }
        }
        if (!sectionName) return
        if (predicate(rootPointer)) {
            outline[sectionName] ??= {
                images: []
            }
            const contextProps = builderComponents.getData(rootPointer, true, ['image', 'text'])
            contextProps.forEach(context => {
                const {prop, path, value: data} = context
                const mappedId = getNextIdForPrefix(context.type, typeCounter)
                if (context.type === 'text') return (outline[sectionName][mappedId] = data)
                else if (toAdd) {
                    const measurements =
                        path !== undefined
                            ? innerElements.getInnerElementMeasurements(path)
                            : viewer.getComponentMeasurements(rootPointer as CompRef)
                    idMap[mappedId] = {pointer: rootPointer, prop, path}
                    oldUrlMap[mappedId] = data?.uri
                    const {width, height} = measurements
                    outline[sectionName].images.push({id: mappedId, width, height})
                }
            })
        }
    }

    const outline: BuilderImageOutlineData = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldUrlMap = {}
    const pagePointer = pointers.structure.getPageOfComponent(componentPointer)
    getContentRecursively(
        pagePointer,
        componentPointer,
        idMap,
        oldUrlMap,
        typeCounter,
        undefined,
        predicateBuilder,
        outline
    )
    const outlineOfSectionsWithImages = Object.fromEntries(
        Object.entries(outline).filter(([, val]) => val.images.length > 0)
    )
    return {outline: outlineOfSectionsWithImages, idMap, oldUrlMap}
}

export const injectImages = (
    createExtArgs: CreateExtArgs,
    {data: images, idMap}: ImageGenerationServerlessResponseWithIdMap
) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    Object.entries(images).forEach(([id, url]) => {
        const {pointer, prop, path} = idMap[id]
        if (!pointer) return
        const dataToUpdate = () => {
            const imageData = schemaAPI.createItemAccordingToSchema('Builder.Image', DATA_TYPES.data)
            return {data: {[prop]: _.merge({uri: url}, imageData)}}
        }
        injectData(createExtArgs, pointer, path, dataToUpdate)
    })
}

const validateResult = (images: Record<string, string>, idMap: IdMap): ValidationResult => {
    const missingImagesUrl = Object.keys(idMap).filter(img => !images.hasOwnProperty(img))
    return {
        isValid: missingImagesUrl.length === 0,
        reason: missingImagesUrl.length === 0 ? '' : `failed to generate urls for ${missingImagesUrl}`
    }
}

export const getImageInjectionForBuilder = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    photoTheme: string | undefined
): Promise<ImageGenerationServerlessResponseWithIdMap> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    const {outline, idMap, oldUrlMap} = getBuilderImageOutlineData(createExtArgs, componentPointer)
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    try {
        const res = await fetchImageUrlsForBuilder(
            serverFacade,
            userRequest,
            outline,
            siteType,
            businessName,
            siteDescription,
            photoTheme,
            msid
        )
        const imageUrls: Record<string, string> = JSON.parse(res)
        const validation = validateResult(imageUrls, idMap)
        if (!validation.isValid)
            reportBIError(
                logger,
                aiImageInjectionValidationErrorType,
                aiImageInjectionValidationErrorMessage,
                imageUrls,
                validation
            )
        injectImages(createExtArgs, {idMap, data: imageUrls})
        const staticImageUrls: Record<string, string> = {}
        await Promise.all(
            Object.entries(imageUrls).map(async ([id, url]) => {
                const {operationStatus, url: staticUrl, error} = (await uploadImage(logger, serverFacade, url)) ?? {}
                if (operationStatus === UPLOAD_STATUS.READY) staticImageUrls[id] = staticUrl
                else {
                    staticImageUrls[id] = oldUrlMap[id]
                    reportImageUploadError(logger, aiImageUploadErrorType, aiImageUploadErrorMessage, {
                        imageId: idMap[id],
                        tempUrl: url,
                        error
                    })
                }
            })
        )

        return {
            idMap,
            data: staticImageUrls
        }
    } catch (e: any) {
        const reportedError = getReportableFromError(e, {
            errorType: aiImageUnexpectedError,
            message: aiImageUnexpectedErrorMessage,
            tags: {
                aiImageInjection: true
            }
        })
        logger.captureError(reportedError)
        throw reportedError
    }
}
